import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PauseCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const day = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

function Items({ items }) {
  return (
    <ul className="mt-4 space-y-2 text-sm" data-testid="keep-paused-items">
      {items.map((i) => (
        <li key={i.id} className="border-l-4 border-black pl-2" data-testid={`keep-paused-item-${i.id}`}>
          <div className="font-bold">{i.kind === "vendor" ? "Affiliate" : "Funnel"}: {i.target_name}</div>
          <div className="text-xs text-zinc-600">{i.what}{!i.active && " — already turned back on"}</div>
        </li>
      ))}
    </ul>
  );
}

// Public page behind the "Keep paused 30 more days" link in the resume-reminder email.
export default function KeepPaused() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("ask");
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (!token) {
      setInfo({ error: "This link is missing its code. Use the link from the email." });
      return;
    }
    api.get("/public/paused-traffic/keep", { params: { t: token } })
      .then((r) => setInfo(r.data))
      .catch((e) => setInfo({ error: formatApiError(e?.response?.data?.detail) || "This link is no longer valid." }));
  }, [token]);
  const keep = async () => {
    setState("busy");
    try {
      const { data } = await api.post("/public/paused-traffic/keep", { token });
      setResult(data);
      setState("done");
    } catch (e) {
      setMsg(formatApiError(e?.response?.data?.detail) || "Something went wrong. Please try again.");
      setState("error");
    }
  };
  const active = (info?.items || []).filter((i) => i.active);
  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-100 p-6" data-testid="keep-paused-page">
      <div className="brut-card w-full max-w-md bg-white p-8">
        <PauseCircle size={28} weight="bold" />
        <h1 className="mt-3 font-chivo text-2xl font-black uppercase tracking-tight">Keep paused {info?.days || 30} more days</h1>
        {!info && <p className="label-mono mt-3">Loading…</p>}
        {info?.error && <p className="mt-3 text-sm text-red-700" data-testid="keep-paused-invalid">{info.error}</p>}
        {info?.items && state !== "done" && (
          <>
            <p className="mt-3 text-sm text-zinc-700">This stays paused and you won&apos;t get reminder emails about it until {day(new Date(Date.now() + (info.days || 30) * 86400000).toISOString())}.</p>
            <Items items={info.items} />
            {state === "error" && <p className="mt-3 text-sm text-red-700" data-testid="keep-paused-error">{msg}</p>}
            <button type="button" onClick={keep} disabled={!active.length || state === "busy"} data-testid="keep-paused-confirm"
                    className="mt-6 w-full border-2 border-black bg-black px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-zinc-800 disabled:opacity-50">
              {state === "busy" ? "Saving…" : active.length > 1 ? `Keep all ${active.length} paused` : "Keep paused"}
            </button>
            {!active.length && <p className="mt-3 text-xs text-zinc-500" data-testid="keep-paused-nothing">Nothing here is paused any more.</p>}
          </>
        )}
        {state === "done" && (
          <p className="mt-3 text-sm text-zinc-700" data-testid="keep-paused-done">
            Done. {result.count === 1 ? "It stays" : `All ${result.count} stay`} paused and you won&apos;t get reminders until {day(result.snoozed_until)}.
          </p>
        )}
        <Link to="/admin/paused-traffic" className="mt-6 block text-xs underline" data-testid="keep-paused-open-app">Open Paused Traffic (sign in)</Link>
      </div>
    </div>
  );
}
